// ============================================================================
// VerifyEmail — /verify-email?token=XXX
// Landed from the "Verify your email" email link.
// ============================================================================
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, Alert, Stack,
  alpha, useTheme, CircularProgress,
} from '@mui/material';
import { AutoAwesome, CheckCircle, MarkEmailRead } from '@mui/icons-material';
import { authApi } from '../services/apiService';
import { useAuth } from '../context/AuthContext';

export default function VerifyEmail() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const { refreshUser } = useAuth();

  const [state, setState] = useState(token ? 'verifying' : 'invalid'); // verifying | done | error | invalid
  const [error, setError] = useState('');
  const firedRef = useRef(false);

  useEffect(() => {
    if (!token || firedRef.current) return;
    firedRef.current = true;
    authApi.verifyEmail(token)
      .then(() => {
        setState('done');
        // Refresh the signed-in user so the unverified banner disappears
        if (refreshUser) refreshUser().catch(() => {});
      })
      .catch((err) => {
        setError(err?.response?.data?.error || 'Verification failed. The link may have expired.');
        setState('error');
      });
  }, [token, refreshUser]);

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: `linear-gradient(135deg, ${alpha('#1a3a6e', 0.08)}, ${alpha('#e8722a', 0.05)})`,
      p: 2,
    }}>
      <Card elevation={0} sx={{
        width: '100%', maxWidth: 420, borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
      }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <Box sx={{
              width: 44, height: 44, borderRadius: 2.5,
              background: 'linear-gradient(135deg, #1a3a6e, #e8722a)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <AutoAwesome sx={{ color: '#fff', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={800} lineHeight={1.2}>The Cycle</Typography>
              <Typography variant="caption" color="text.secondary">Email verification</Typography>
            </Box>
          </Box>

          {state === 'invalid' && (
            <Alert severity="error">
              Invalid verification link. <Box component="span" sx={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={() => navigate('/')}>Return home</Box>
            </Alert>
          )}

          {state === 'verifying' && (
            <Stack spacing={2} alignItems="center" sx={{ py: 2 }}>
              <CircularProgress size={40} />
              <Typography variant="body2" color="text.secondary">Verifying your email…</Typography>
            </Stack>
          )}

          {state === 'done' && (
            <Stack spacing={2} alignItems="center" sx={{ py: 1 }}>
              <CheckCircle sx={{ fontSize: 52, color: 'success.main' }} />
              <Typography variant="h6" fontWeight={700} textAlign="center">Email verified!</Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Your account is all set. Welcome to The Cycle.
              </Typography>
              <Button
                fullWidth
                variant="contained"
                startIcon={<MarkEmailRead />}
                onClick={() => navigate('/')}
                sx={{
                  fontWeight: 700, borderRadius: 2, textTransform: 'none', py: 1.2,
                  background: 'linear-gradient(135deg, #1a3a6e, #e8722a)',
                  '&:hover': { background: 'linear-gradient(135deg, #162f5a, #c96520)' },
                }}
              >
                Go to The Cycle
              </Button>
            </Stack>
          )}

          {state === 'error' && (
            <Stack spacing={2}>
              <Alert severity="error">{error}</Alert>
              <Typography variant="body2" color="text.secondary">
                Sign in and use "Resend verification email" to get a fresh link.
              </Typography>
              <Button variant="outlined" onClick={() => navigate('/')} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>
                Return home
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
